import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from "react-router-dom";
import SubNav from "../components/nav";
import SEO from '../components/SEO';
import { ArrowRight, MapPin, Camera, Filter, Download, Calendar, FileText, Film, PenTool } from 'lucide-react';
import '../styles/LocationScouting.css';
// import { baseUrl } from '../config';

// Section component
const Section = memo(({ section, isActive, refCallback }) => (
    <div
        ref={refCallback}
        className={`how-to-use-location__section ${isActive ? 'how-to-use-location__animate-in' : ''}`}
        aria-label={section.title}
        tabIndex={0}
    >
        <div className="how-to-use-location__section-icon">{section.icon}</div>
        <div className="how-to-use-location__section-content">
            <h2 className="how-to-use-location__section-title">{section.title}</h2>
            <p className="how-to-use-location__section-text">{section.content}</p>
            <p className="how-to-use-location__section-details">{section.details}</p>
        </div>
        <img
            className="how-to-use-location__section-image"
            src={section.img}
            alt={`${section.title} illustration`}
        />
        {section.iframe}
    </div>
));

Section.propTypes = {
    section: PropTypes.shape({
        title: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
        details: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        img: PropTypes.string.isRequired,
        iframe: PropTypes.element.isRequired,
    }).isRequired,
    isActive: PropTypes.bool.isRequired,
    refCallback: PropTypes.func.isRequired,
};

const HowToUseLocationScouting = () => {
    const sectionRefs = useRef([]);
    const [activeSection, setActiveSection] = useState(0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = sectionRefs.current.indexOf(entry.target);
                        setActiveSection(index);
                    }
                });
            },
            { threshold: 0.3 }
        );

        const currentRefs = sectionRefs.current;
        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
            observer.disconnect();
        };
    }, []);

    // Function to handle clicking on sidebar items
    const handleSectionClick = (index) => {
        const element = sectionRefs.current[index];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(index);
        }
    };

    const sections = useMemo(() => [
        {
            title: "Define Your Project",
            content: `Start by providing essential details about your project including the genre, budget level, and production type. This initial information helps our AI understand your creative vision and production needs.`,
            details: `For example, indicate whether your project is a "low-budget horror film," "documentary series," or "high-end commercial" to get location recommendations that match your production level and style.`,
            icon: <FileText size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="define-project-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Specify Genre & Mood",
            content: `Select your project's genre and desired visual aesthetic or mood. Our AI uses this information to identify locations that create the right atmosphere for your story, whether that's urban dystopia, pastoral romance, or corporate thriller.`,
            details: `Tip: Be specific about visual tone - terms like "gritty noir," "sun-drenched period piece," or "minimalist sci-fi" help our system find locations with lighting conditions and architectural features that match your vision.`,
            icon: <Film size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="specify-genre-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Upload Script Requirements",
            content: `Upload your script or scene breakdowns to extract specific location requirements. Our AI analyzes your content to identify key location needs for each scene, from "abandoned warehouse at night" to "upscale suburban kitchen."`,
            details: `You can highlight specific sections of your script for the AI to focus on, or let it analyze the entire document to create a comprehensive location needs assessment.`,
            icon: <PenTool size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="upload-script-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Browse Matched Locations",
            content: `Our AI presents a curated list of locations perfectly matched to your project requirements. Each recommendation includes real-time availability information, so you'll immediately know which locations are available during your production dates.`,
            details: `The system ranks locations based on how well they match your specific needs, taking into account factors like visual style, practical requirements, and budget constraints.`,
            icon: <MapPin size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="browse-locations-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Check Real-Time Availability",
            content: `View detailed availability calendars for each location showing open dates, existing bookings, and blackout periods. You can filter locations based on your production schedule to see only options that align with your timeline.`,
            details: `The availability feature integrates with major production calendars, allowing you to check for scheduling conflicts across your entire production plan with a single click.`,
            icon: <Calendar size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="check-availability-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Explore Virtual Site Visits",
            content: `Take virtual tours of potential filming locations with our immersive 3D walkthroughs and high-resolution panoramic images. Assess spatial dimensions, lighting conditions, and acoustic properties without leaving your office.`,
            details: `Our virtual site visits include valuable insights such as sun path animations showing how natural light moves through the space at different times of day and seasons, helping you plan optimal shooting schedules.`,
            icon: <Camera size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="virtual-visits-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Compare Location Options",
            content: `Use our side-by-side comparison tool to evaluate multiple locations based on factors like cost, technical specifications, permit requirements, and logistical considerations. This feature makes it easy to narrow down your options.`,
            details: `Tip: The comparison tool includes a unique "production value score" that estimates how much visual impact each location will add to your project relative to its cost.`,
            icon: <Filter size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="compare-locations-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Export Location Reports",
            content: `Generate comprehensive location reports with all essential details, including contact information, permit requirements, and technical specifications. These reports can be downloaded in various formats (PDF, PPT) for sharing with your production team.`,
            details: `Location reports include critical information like power availability, noise restrictions, parking options, and nearby amenities – everything your production team needs to assess feasibility.`,
            icon: <Download size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="export-reports-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        }
    ], []);

    return (
        <>
            <SEO
              title="Location Scouting Guide"
              description="Learn how to find, evaluate, and secure filming locations for your production using ProducerBuddy's location tools."
              url="https://producerbuddy.pilomation.com/locationHowTo"
            />
            <SubNav />
            <div className="how-to-use-location">
                <div className="how-to-use-location__container">
                    <h1 className="how-to-use-location__title">How to Use Producer Buddy: Location Scouting</h1>
                    
                    <div className="how-to-use-location__intro">
                        <p>Our AI-powered Location Scouting tool streamlines the process of finding the perfect filming locations for your production. By analyzing your project details, genre specifications, and script requirements, we'll help you discover locations that perfectly match your creative vision while meeting practical production needs.</p>
                    </div>

                    <div className="how-to-use-location__layout">
                        {/* Sidebar */}
                        <div className="how-to-use-location__sidebar" role="navigation" aria-label="Section navigation">
                            {sections.map((section, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSectionClick(index)}
                                    className={`how-to-use-location__sidebar-item ${activeSection === index ? 'active' : ''}`}
                                    aria-current={activeSection === index ? 'page' : undefined}
                                    type="button"
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <span>{section.title}</span>
                                        {activeSection === index && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Timeline */}
                        <div className="how-to-use-location__timeline">
                            {sections.map((section, index) => (
                                <Section
                                    key={index}
                                    section={section}
                                    isActive={activeSection === index}
                                    refCallback={(el) => (sectionRefs.current[index] = el)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Call to action */}
                    <div className="how-to-use-location__cta-container">
                        <NavLink to="/Services" className="how-to-use-location__cta-button">
                            Explore Features
                            <ArrowRight className="ml-2" />
                        </NavLink>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HowToUseLocationScouting;
